import { Component, computed, inject, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import {
  AdminDto,
  AdminsClient,
  DateOverrideDto,
  InviteAdminsCommand,
  SetDateOverridesCommand,
  SetWorkingTimeCommand,
  WorkingTimeClient,
} from '../../../infrastructure/shared/model/fss.api';
import { InviteAdminsDialog } from './invite-admins-dialog/invite-admins-dialog';
import { DateOverrideDialog } from './date-override-dialog/date-override-dialog';
import { PageComponent } from '../../../infrastructure/shared/components/page/page';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../infrastructure/shared/ui/button/button';
import { FieldComponent } from '../../../infrastructure/shared/ui/field/field';
import { CalendarComponent } from '../../../infrastructure/shared/ui/calendar/calendar';
import { UserCircle } from '../../../infrastructure/shared/icons/user-circle/user-circle';
import { Plus } from '../../../infrastructure/shared/icons/plus/plus';
import { Trash01 } from '../../../infrastructure/shared/icons/trash-01/trash-01';
import { Clock } from '../../../infrastructure/shared/icons/clock/clock';
import { AlertService } from '../../../infrastructure/shared/services/alert.service';
import { UserService } from '../../../infrastructure/shared/services/user.service';
import { LoaderService } from '../../../infrastructure/shared/components/loader/loader.service';
import { ConfirmActionDialog } from '../../../infrastructure/shared/components/confirm-action-dialog/confirm-action-dialog';
import { getDateFormatedSrFullMonth } from '../../../infrastructure/shared/services/date.service';

interface DayOption {
  value: number;
  label: string;
}

const DAYS: DayOption[] = [
  { value: 1, label: 'Pon' },
  { value: 2, label: 'Uto' },
  { value: 3, label: 'Sre' },
  { value: 4, label: 'Čet' },
  { value: 5, label: 'Pet' },
  { value: 6, label: 'Sub' },
  { value: 7, label: 'Ned' },
];

const MONTHS_SR_SHORT = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAJ',
  'JUN',
  'JUL',
  'AVG',
  'SEP',
  'OKT',
  'NOV',
  'DEC',
];

@Component({
  standalone: true,
  selector: 'app-admin-settings',
  imports: [
    PageComponent,
    ConfirmActionDialog,
    ButtonComponent,
    FieldComponent,
    CalendarComponent,
    UserCircle,
    InviteAdminsDialog,
    DateOverrideDialog, Plus, Trash01, Clock],
  templateUrl: './admin-settings.html',
  styleUrl: './admin-settings.scss',
})
export class AdminSettings {
  private readonly _admins = inject(AdminsClient);
  private readonly _workingTime = inject(WorkingTimeClient);
  private readonly _alert = inject(AlertService);
  private readonly _user = inject(UserService);
  private readonly _loader = inject(LoaderService);

  protected readonly currentUserId = this._user.getUserData.id;

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;
  protected readonly DAYS = DAYS;

  protected admins = signal<AdminDto[]>([]);
  protected inviteDialogVisible = signal<boolean>(false);
  protected adminToDeactivate = signal<AdminDto | undefined>(undefined);

  protected fromCtrl = new FormControl<Date | null>(null);
  protected toCtrl = new FormControl<Date | null>(null);
  protected selectedDays = signal<Set<number>>(new Set());

  protected dateOverrides = signal<DateOverrideDto[]>([]);
  protected dateOverrideDialogVisible = signal<boolean>(false);

  protected selectedDaysCount = computed(() => this.selectedDays().size);

  constructor() {
    this._loadAdmins();
    this._loadWorkingTime();
    this._loadDateOverrides();
  }

  protected toggleDay(day: number): void {
    this.selectedDays.update((set) => {
      const next = new Set(set);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });
  }

  protected isDaySelected(day: number): boolean {
    return this.selectedDays().has(day);
  }

  protected adminDisplayName(admin: AdminDto): string {
    const local = (admin.email ?? '').split('@')[0] ?? '';
    if (!local) return admin.email ?? '';
    return local
      .split(/[._-]/)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }

  protected confirmDeactivateAdmin(): void {
    const admin = this.adminToDeactivate();
    this.adminToDeactivate.set(undefined);
    if (admin) this.deactivateAdmin(admin);
  }

  protected async deactivateAdmin(admin: AdminDto): Promise<void> {
    this._loader.setLoadingText = 'Deaktivacija admina...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._admins.deactivateAdmin(admin.id));
      this._alert.addSuccessMsg(res.result ?? 'Admin je deaktiviran.');
      this.admins.update((list) => list.filter((a) => a.id !== admin.id));
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  protected addAdmin(): void {
    this.inviteDialogVisible.set(true);
  }

  protected async onInviteSubmitted(cmd: InviteAdminsCommand): Promise<void> {
    this._loader.setLoadingText = 'Slanje pozivnica...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._admins.inviteAdmins(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Pozivnice su poslate.');
      this.inviteDialogVisible.set(false);
      await this._loadAdmins();
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  protected addDateOverride(): void {
    this.dateOverrideDialogVisible.set(true);
  }

  protected onDateOverrideSubmitted(overrides: DateOverrideDto[]): void {
    this.dateOverrides.update((list) =>
      [...list, ...overrides].sort((a, b) => {
        const ad = a.date ? new Date(a.date).getTime() : 0;
        const bd = b.date ? new Date(b.date).getTime() : 0;
        return ad - bd;
      }),
    );
    this.dateOverrideDialogVisible.set(false);
  }

  protected removeDateOverride(index: number): void {
    this.dateOverrides.update((list) => list.filter((_, i) => i !== index));
  }

  protected formatOverrideMonth(date: Date | undefined): string {
    if (!date) return '';
    return MONTHS_SR_SHORT[date.getMonth()] ?? '';
  }

  protected formatOverrideDay(date: Date | undefined): string {
    if (!date) return '';
    return String(date.getDate());
  }

  protected formatOverrideLabel(date: Date | undefined): string {
    if (!date) return '';
    return getDateFormatedSrFullMonth(date);
  }

  protected formatOverrideIntervals(override: DateOverrideDto): string {
    const intervals = override.intervals ?? [];
    if (intervals.length === 0) return 'Ceo dan';
    return intervals
      .map((i) => `${this._trimSeconds(i.from)} - ${this._trimSeconds(i.to)}`)
      .join(', ');
  }

  private _trimSeconds(time: string | undefined): string {
    if (!time) return '';
    const parts = time.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
  }

  protected async save(): Promise<void> {
    const from = this.fromCtrl.value;
    const to = this.toCtrl.value;
    if (!from || !to || this.selectedDays().size === 0) {
      this._alert.addFailedMsg('Popunite radno vreme i izaberite radne dane.');
      return;
    }

    const wtCmd = new SetWorkingTimeCommand({
      from: this._toTimeOnly(from),
      to: this._toTimeOnly(to),
      days: Array.from(this.selectedDays()).sort((a, b) => a - b),
    });

    const doCmd = new SetDateOverridesCommand({
      dateOverrides: this.dateOverrides(),
    });

    this._loader.setLoadingText = 'Čuvanje izmena...';
    this._loader.setShowLoader = true;
    try {
      const [wtRes] = await Promise.all([
        firstValueFrom(this._workingTime.setWorkingTime(wtCmd)),
        firstValueFrom(this._workingTime.setDateOverrides(doCmd)),
      ]);
      this._alert.addSuccessMsg(wtRes.result ?? 'Izmene su sačuvane.');
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  private async _loadAdmins(): Promise<void> {
    const res = await firstValueFrom(this._admins.getAdmins());
    this.admins.set(res.result?.admins ?? []);
  }

  private async _loadWorkingTime(): Promise<void> {
    const res = await firstValueFrom(this._workingTime.getWorkingTime());
    const wt = res.result;
    if (!wt) return;
    this.fromCtrl.setValue(this._toDate(wt.from));
    this.toCtrl.setValue(this._toDate(wt.to));
    this.selectedDays.set(new Set(wt.workingDays ?? []));
  }

  private async _loadDateOverrides(): Promise<void> {
    const res = await firstValueFrom(this._workingTime.getDateOverrides());
    this.dateOverrides.set(res.result?.dateOverrides ?? []);
  }

  private _toDate(value: string | undefined): Date | null {
    if (!value) return null;
    const [h, m, s] = value.split(':').map(Number);
    const d = new Date();
    d.setHours(h ?? 0, m ?? 0, s ?? 0, 0);
    return d;
  }

  private _toTimeOnly(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
}
